import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";

const SERVICE_TYPES = ["2-Man Home Delivery", "Man & Van / House Removal", "Courier & Pallet Delivery", "Construction & Site Delivery", "Retail & E-commerce Delivery", "Other"];
const ITEM_TYPES = ["Furniture", "White Goods / Appliances", "Parcels / Pallets", "Construction Materials", "Household Items", "Other"];
const CREW_SIZES = ["1-Man", "2-Man", "3-Man+"];

const points = [
  "Trained two-man crews for careful handling",
  "Room-of-choice delivery, assembly & installation on request",
  "Packaging removed and recycled, old items taken away",
  "One dedicated coordinator — no call centres",
];

const initialForm = {
  company_name: "", contact_name: "", email: "", phone: "", service_type: "", item_type: "",
  crew_size: "2-Man", collection_location: "", delivery_location: "", preferred_date: "", message: "",
};

export const HireUs = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.email || !form.service_type || !form.delivery_location) {
      toast.error("Please fill in your name, email, service needed and delivery location.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Booking request received! Our team will contact you within one working day.");
      setForm(initialForm);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="hire-us" className="py-20 sm:py-24 bg-slate-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-cyan-400 mb-4">For businesses & homeowners</p>
          <h2 className="text-4xl sm:text-5xl tracking-tight font-semibold text-white">Need a van and crew? Book us.</h2>
          <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-lg">
            Tell us what needs moving and where — we'll send a fully-equipped van and a trained crew, often within 24 hours.
          </p>
          <ul className="mt-8 space-y-4">
            {points.map((p) => (
              <li key={p} className="flex items-start gap-3 text-slate-200">
                <CheckCircle2 size={20} className="text-cyan-400 mt-0.5 shrink-0" />
                <span className="text-sm sm:text-base">{p}</span>
              </li>
            ))}
          </ul>
          <img src="/assets/box-van.jpg"
            alt="PRGL box van ready for a delivery job" className="mt-10 rounded-lg w-full h-52 object-cover hidden lg:block" />
        </motion.div>
        <motion.form onSubmit={submit} data-testid="hire-us-form"
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-lg p-7 sm:p-9 shadow-[0_8px_24px_rgba(15,23,42,0.25)]">
          <h3 className="text-2xl font-medium text-slate-900">Book a van & crew</h3>
          <p className="text-sm text-slate-500 mt-1 mb-7">Takes under a minute. No obligation.</p>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="company_name">Full name / Company *</Label>
              <Input id="company_name" data-testid="hire-form-company" value={form.company_name} onChange={set("company_name")} placeholder="John Smith or Acme Retail Ltd" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" data-testid="hire-form-email" value={form.email} onChange={set("email")} placeholder="jane@example.co.uk" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" data-testid="hire-form-phone" value={form.phone} onChange={set("phone")} placeholder="07xxx xxxxxx" />
            </div>
            <div className="space-y-2">
              <Label>Service needed *</Label>
              <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger data-testid="hire-form-service">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>What's being moved?</Label>
              <Select value={form.item_type} onValueChange={(v) => setForm({ ...form, item_type: v })}>
                <SelectTrigger data-testid="hire-form-item-type">
                  <SelectValue placeholder="Select item type" />
                </SelectTrigger>
                <SelectContent>
                  {ITEM_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Crew size</Label>
              <Select value={form.crew_size} onValueChange={(v) => setForm({ ...form, crew_size: v })}>
                <SelectTrigger data-testid="hire-form-crew-size">
                  <SelectValue placeholder="Select crew size" />
                </SelectTrigger>
                <SelectContent>
                  {CREW_SIZES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="collection_location">Collection location</Label>
              <Input id="collection_location" data-testid="hire-form-collection" value={form.collection_location} onChange={set("collection_location")} placeholder="Postcode or address" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="delivery_location">Delivery location *</Label>
              <Input id="delivery_location" data-testid="hire-form-delivery" value={form.delivery_location} onChange={set("delivery_location")} placeholder="Postcode or address" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="preferred_date">Preferred date</Label>
              <Input id="preferred_date" type="date" data-testid="hire-form-date" value={form.preferred_date} onChange={set("preferred_date")} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="message">Anything else?</Label>
              <Textarea id="message" data-testid="hire-form-message" value={form.message} onChange={set("message")} placeholder="Access notes, item dimensions, assembly needed..." rows={3} />
            </div>
          </div>
          <Button type="submit" disabled={submitting} data-testid="hire-us-submit"
            className="mt-7 w-full bg-blue-600 hover:bg-blue-700 text-white rounded-md h-12 text-base">
            {submitting ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : null}
            {submitting ? "Sending..." : "Book a Van & Crew"}
          </Button>
        </motion.form>
      </div>
    </section>
  );
};
